import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
} from 'lexical';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';
import { orderBy } from 'utils/arrayUtils';

interface InsertVariableButtonProps {
  disabled: boolean;
  toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'];
}

const InsertVariableButton = ({
  disabled,
  toolbarSettings: { features },
}: InsertVariableButtonProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();

  if (!features?.variables?.length) {
    return null;
  }

  const handleInsertVariableClick = (variableToInsert: unknown) => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        // If there's a selection, use it to insert the variable
        selection.removeText();
        selection.insertText(variableToInsert as string);
      } else {
        // Otherwise, inserts the variable in the editor's first child
        const newNode = $createTextNode(variableToInsert as string);
        const root = $getRoot();
        const rootFirstChild = root.getFirstChild();

        if ($isParagraphNode(rootFirstChild)) {
          rootFirstChild.splice(0, 0, [newNode]);
        } else {
          const paragraph = $createParagraphNode();
          paragraph.append(newNode);
          root.splice(0, 0, [paragraph]);
        }
      }
    });
  };

  const variables = orderBy(features.variables, [
    { field: 'label', order: 'asc' },
  ]);

  return (
    <DropdownButton
      bsStyle="default"
      className="toolbar-item toolbar-item--green toolbar-item--vertical-dropdown"
      data-testid="toolbar-button-variables"
      disabled={disabled}
      id="rich-text-editor-insert-variable"
      noCaret
      title={t('form.editor.text.insertVariable')}
    >
      {variables.map((variable) => (
        <MenuItem
          eventKey={variable.value}
          key={variable.value}
          onSelect={handleInsertVariableClick}
        >
          {variable.label}
        </MenuItem>
      ))}
    </DropdownButton>
  );
};

export default InsertVariableButton;
